import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import { useStore } from './AppStore'
import type { Track } from './types'

interface Player {
  current?: Track
  playing: boolean
  elapsed: number
  progress: number
  play: (id: string) => void
  toggle: () => void
  next: () => void
  prev: () => void
  seek: (sec: number) => void
  stop: () => void
}

const Ctx = createContext<Player | null>(null)

export function PlayerProvider({ children }: { children: ReactNode }) {
  const { state } = useStore()
  const tracks = state.tracks

  const [currentId, setCurrentId] = useState<string | undefined>()
  const [playing, setPlaying] = useState(false)
  const [elapsed, setElapsed] = useState(0)
  const audioRef = useRef<HTMLAudioElement | null>(null)

  const current = tracks.find((t) => t.id === currentId)
  const index = tracks.findIndex((t) => t.id === currentId)

  const stop = useCallback(() => {
    audioRef.current?.pause()
    audioRef.current = null
    setPlaying(false)
    setElapsed(0)
  }, [])

  const play = useCallback(
    (id: string) => {
      const track = tracks.find((t) => t.id === id)
      if (!track) return
      audioRef.current?.pause()
      audioRef.current = null
      setCurrentId(id)
      setElapsed(0)
      setPlaying(true)
      // Seeded tracks have no file behind them — they run on the simulated
      // clock below so the UI is still demonstrable before anything is uploaded.
      if (track.url) {
        const audio = new Audio(track.url)
        audio.play().catch(() => setPlaying(false))
        audioRef.current = audio
      }
    },
    [tracks],
  )

  const next = useCallback(() => {
    if (!tracks.length) return
    const i = index < 0 ? 0 : (index + 1) % tracks.length
    play(tracks[i].id)
  }, [index, tracks, play])

  const prev = useCallback(() => {
    if (!tracks.length) return
    const i = index <= 0 ? tracks.length - 1 : index - 1
    play(tracks[i].id)
  }, [index, tracks, play])

  const toggle = useCallback(() => {
    if (!current) {
      if (tracks[0]) play(tracks[0].id)
      return
    }
    setPlaying((p) => {
      const nextPlaying = !p
      if (audioRef.current) {
        if (nextPlaying) void audioRef.current.play().catch(() => {})
        else audioRef.current.pause()
      }
      return nextPlaying
    })
  }, [current, tracks, play])

  const seek = useCallback((sec: number) => {
    setElapsed(sec)
    if (audioRef.current) audioRef.current.currentTime = sec
  }, [])

  /* Clock: mirrors the real element when there is one, simulates otherwise. */
  useEffect(() => {
    if (!playing || !current) return
    const id = window.setInterval(() => {
      if (audioRef.current) {
        setElapsed(audioRef.current.currentTime)
        if (audioRef.current.ended) next()
        return
      }
      setElapsed((e) => {
        if (e + 0.5 >= current.durationSec) {
          next()
          return 0
        }
        return e + 0.5
      })
    }, 500)
    return () => window.clearInterval(id)
  }, [playing, current, next])

  useEffect(() => () => audioRef.current?.pause(), [])

  const value = useMemo<Player>(
    () => ({
      current,
      playing,
      elapsed,
      progress: current?.durationSec ? Math.min(100, (elapsed / current.durationSec) * 100) : 0,
      play,
      toggle,
      next,
      prev,
      seek,
      stop,
    }),
    [current, playing, elapsed, play, toggle, next, prev, seek, stop],
  )

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>
}

export function usePlayer(): Player {
  const ctx = useContext(Ctx)
  if (!ctx) throw new Error('usePlayer must be used inside <PlayerProvider>')
  return ctx
}
